import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CurrencyCode } from '@bbpos/types';
import { Button, Skeleton } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { changeKind, describeChanges } from './change-log-format';

const KIND_TONE: Record<ReturnType<typeof changeKind>, string> = {
  Created: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300',
  'Added variants': 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Deactivated: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
  Edited: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
};

/**
 * The product's Change log tab (POS comments item 4, Manager and above): who
 * changed the product or one of its variants, when, and what moved (old → new).
 */
export function ProductChangeLog({ productId, productName, currency }: { productId: string; productName: string; currency: CurrencyCode }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['product', productId, 'change-log', page],
    queryFn: () => productService.getChangeLog(productId, { page, limit: 20 }),
  });
  const entries = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;
  const money = (m: number) => formatPaise(m, currency);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="rect" height={64} />)}
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className={cn(GLASS_FORM, 'p-8 text-center')}>
        <p className="font-medium text-neutral-700 dark:text-neutral-200">No changes recorded yet</p>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Edits to this product and its variants will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-in">
      <ul className="space-y-3">
        {entries.map((e) => {
          const kind = changeKind(e);
          const lines = describeChanges(e, money);
          return (
            <li key={e.id} className={cn(GLASS_FORM, 'p-4 animate-slide-up transition-all duration-200 hover:shadow-lg')}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', KIND_TONE[kind])}>{kind}</span>
                {e.product_name && e.product_name !== productName && (
                  <span className="font-medium text-neutral-800 dark:text-neutral-100">{e.product_name}</span>
                )}
                <span className="text-neutral-500 dark:text-neutral-400">
                  by {e.user_name ?? 'someone'} · {new Date(e.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {lines === null ? (
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Details were not recorded for edits made before this log existed.</p>
              ) : lines.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {lines.map((l) => (
                    <li key={l.field} className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">{l.field}:</span>{' '}
                      <span className="text-neutral-500 line-through decoration-neutral-300 dark:decoration-neutral-600">{l.from}</span>
                      {' → '}
                      <span className="font-semibold text-neutral-900 dark:text-white">{l.to}</span>
                    </li>
                  ))}
                </ul>
              ) : kind === 'Edited' ? (
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">Saved with no field changes.</p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Newer</Button>
          <span className="text-xs text-neutral-500">Page {page} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Older</Button>
        </div>
      )}
    </div>
  );
}
